import { useState, useEffect, useRef, forwardRef, useCallback } from 'react';
import Select, { components } from 'react-select';
import DatePicker from 'react-datepicker';
import { debounce } from 'lodash';
import { IoCheckmark, IoClose, IoCopyOutline } from "react-icons/io5";
import { IoCalendarOutline } from "react-icons/io5";
import { validate } from "./validation";
import "react-datepicker/dist/react-datepicker.css";
import { BiSolidHide } from "react-icons/bi";
import { BiShow } from "react-icons/bi";

import "./inputs.css";
import { FiCheck, FiUploadCloud, FiFileText } from "react-icons/fi";
import { Tag } from "common/Label";
import { toast } from "react-toastify";

const InternalInput = forwardRef(
  (
    {
      type,
      validPatterns,
      disabled,
      id,
      name,
      value,
      placeholder,
      validationRules,
      skipInitialValidation,
      valueExtractor,
      onValidation,
      onChange,
      onEnter,
      onFocusLoss,
      min,
      max,
      accept,
      isCopyableClass,
    },
    ref,
  ) => {
    const isInit = useRef(true);
    const initialValue = useRef(value);
    const [hasError, setHasError] = useState(false);
    const onValidationWrapper = (isValid, failedResults) => {
      setHasError(!isValid);
      if (onValidation) {
        onValidation(isValid, failedResults);
      }
    };
    useEffect(() => {
      if (validationRules) {
        if (isInit.current && skipInitialValidation && initialValue.current === value) {
          return;
        }
        const failedResults = validate(
          valueExtractor ? valueExtractor(value) : value,
          validationRules,
        );
        onValidationWrapper(failedResults.length === 0, failedResults);
      }
    }, [value, validationRules]);
    useEffect(() => {
      if (isInit.current && initialValue.current !== value) {
        isInit.current = false;
      }
    }, [value]);

    const isValid = (value) => {
      if (validPatterns && validPatterns.length > 0) {
        for (const pattern of validPatterns) {
          if (pattern.test(value)) {
            return true;
          }
        }
        return false;
      }
      return true;
    };
    useEffect(() => {
      if (value !== "" && value !== undefined && !isValid(value)) {
        onChange(value, "");
      }
    }, []);

    const onChangeWrapper = (oldValue, newValue, e) => {
      if (newValue === "" || newValue === undefined || isValid(newValue) || type === "file") {
        onChange(oldValue, newValue, e);
      }
    };

    return (
      <input
        ref={ref}
        disabled={disabled}
        type={type}
        className={[
          hasError
            ? "h-[28px] w-full min-w-44 rounded p-2 py-6 box-border text-[16px] font-medium leading-5 focus:outline-none focus:text-black placeholder:text-[#808080] placeholder:text-[16px] placeholder:font-normal border-2"
            : "h-[28px] w-full min-w-44 rounded p-2 py-6 box-border text-[16px] font-medium leading-5 focus:outline-none focus:text-black placeholder:text-[#808080] placeholder:text-[16px] placeholder:font-normal border-2",
          disabled ? "bg-gray-500 cursor-not-allowed opacity-50" : "",
          isCopyableClass || "",
        ].join(" ")}
        id={id}
        name={name ? name : id}
        value={value ?? ""}
        placeholder={placeholder}
        min={min}
        max={max}
        accept={accept}
        onChange={(e) => onChangeWrapper(`${value}`, e.target.value, e)}
        onBlur={onFocusLoss ?? onEnter}
        onKeyDown={(e) => {
          if (onEnter && e.key === "Enter") {
            onEnter();
          }
        }}
      />
    );
  },
);

export const TextInput = ({
  disabled,
  id,
  name,
  value,
  placeholder,
  validationRules,
  skipInitialValidation,
  onValidation,
  onChange,
  type = "text",
  onEnter,
  min,
  max,
  isCopyable = false,
}) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    if (!value) return;

    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);

        setTimeout(() => {
          setCopied(false);
        }, 3000);
      })
      .catch(() => {
        toast.error("Failed to copy!");
      });
  };

  return (
    <div className="relative w-full">
      <InternalInput
        disabled={disabled}
        type={type}
        id={id}
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        validationRules={validationRules}
        skipInitialValidation={skipInitialValidation}
        onValidation={onValidation}
        onEnter={onEnter}
        min={min}
        max={max}
        isCopyableClass={
          isCopyable &&
            typeof value === "string" &&
            value.length > 0 &&
            value !== "undefined" &&
            value !== "null"
            ? "!pr-9"
            : ""
        }
      />

      {isCopyable &&
        typeof value === "string" &&
        value.length > 0 &&
        value !== "undefined" &&
        value !== "null" && (
          <button
            type="button"
            onClick={handleCopy}
            className="w-9 h-full flex items-center justify-center absolute right-0 top-1/2 -translate-y-1/2 text-gray-500 hover:text-secondary-primary_button_bg transition-colors p-1"
            title={copied ? "Copied" : "Copy to clipboard"}>
            {copied ? (
              <IoCheckmark size={22} className="text-green-500" />
            ) : (
              <IoCopyOutline size={20} />
            )}
          </button>
        )}
    </div>
  );
};

export const TextAreaInput = ({
  disabled,
  id,
  name,
  value,
  placeholder,
  validationRules,
  skipInitialValidation,
  onValidation,
  onChange,
  onEnter,
  onFocusLoss,
  autoSize = true,
  rows = 1,
  resizable = false,
}) => {
  const isInit = useRef(true);
  const initialValue = useRef(value);
  const onValidationWrapper = (isValid, failedResults) => {
    if (onValidation) {
      onValidation(isValid, failedResults);
    }
  };
  useEffect(() => {
    if (validationRules) {
      if (isInit.current && skipInitialValidation && initialValue.current === value) {
        return;
      }
      const failedResults = validate(value, validationRules);
      onValidationWrapper((failedResults.length === 0), failedResults);
    }
  }, [value, validationRules]);

  useEffect(() => {
    if (isInit.current && initialValue.current !== value) {
      isInit.current = false;
    }
  }, [value]);

  return (
    <textarea disabled={disabled}
      className={[autoSize ? 'w-full h-24 min-w-80 rounded p-2 py-4 box-border text-[16px] font-medium leading-5 focus:outline-none focus:text-black placeholder:text-placeholder_color placeholder:text-[16px] placeholder:font-bold placeholder:opacity-50 border-2' : ''].join(' ')}
      id={id}
      value={value === null ? undefined : value}
      rows={rows}
      style={{ resize: resizable ? 'vertical' : 'none', minHeight: `${rows * 2.5}rem` }}
      name={name ? name : id}
      placeholder={placeholder}
      onChange={e => onChange(`${value}`, e.target.value)}
      onBlur={onFocusLoss ?? onEnter}
      onKeyDown={e => {
        if (onEnter && e.key === 'Enter') {
          onEnter();
        }
      }}
    />
  );
};

export const PasswordInput = ({
  disabled,
  id,
  name,
  value,
  placeholder,
  validationRules,
  skipInitialValidation,
  onValidation,
  onChange,
  onEnter,
}) => {
  const [isPasswordVisible, setPasswordVisible] = useState(false);

  const togglePasswordVisibility = () => {
    setPasswordVisible((prev) => !prev);
  };

  return (
    <div className="relative">
      <InternalInput
        disabled={disabled}
        type={isPasswordVisible ? "text" : "password"}
        id={id}
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        validationRules={validationRules}
        skipInitialValidation={skipInitialValidation}
        onValidation={onValidation}
        onEnter={onEnter}
      />
      <button
        type="button"
        onClick={togglePasswordVisibility}
        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-black"
        aria-label={isPasswordVisible ? "Hide password" : "Show password"}>
        {isPasswordVisible ? <BiSolidHide size={30} /> : <BiShow size={30} />}
      </button>
    </div>
  );
};

const INPUT_CHECK_SPEED = 100;
const INPUT_FINISHED_LEVEL = 500;

const InternalNumberInput = ({
  disabled,
  id,
  name,
  value,
  validPatterns,
  isEmptyAllowed = true,
  defaultValue = 0,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  placeholder,
  validationRules,
  skipInitialValidation,
  onValidation,
  onChange,
  onEnter,
}) => {
  const takeNumber = (value) =>
    value ? (isNaN(Number(value)) ? undefined : Number(value)) : undefined;
  const [lastInputTimestamp, setLastInputTimestamp] = useState(new Date());
  const [textInput, setTextInput] = useState(value ? `${value}` : "");

  useEffect(() => {
    const monitor = setInterval(() => {
      const isTextInputInSync =
        takeNumber(textInput) === (typeof value === "string" ? takeNumber(value) : value);
      const isLastInputDoneLongAgo =
        new Date().getTime() - lastInputTimestamp.getTime() > INPUT_FINISHED_LEVEL;
      if (!isTextInputInSync && isLastInputDoneLongAgo) {
        setTextInput(value === undefined ? "" : `${value}`);
        clearInterval(monitor);
      }
    }, INPUT_CHECK_SPEED);
    return () => clearInterval(monitor);
  }, [value, textInput, lastInputTimestamp, setTextInput]);

  useEffect(() => {
    const testValue = typeof value === "string" ? takeNumber(value) : value;
    if (!isEmptyAllowed && testValue === undefined) {
      onChange(testValue, defaultValue > min ? defaultValue : min);
      return;
    }
    if (testValue) {
      if (min > testValue) {
        onChange(testValue, min);
      } else if (max < testValue) {
        onChange(testValue, max);
      }
    }
  }, []);

  return (
    <InternalInput
      disabled={disabled}
      type="text"
      validPatterns={validPatterns}
      id={id}
      name={name}
      value={textInput}
      placeholder={placeholder}
      onChange={(o, n) => {
        setTextInput(n);
        setLastInputTimestamp(new Date());
        const newCandidate = takeNumber(n);
        if (newCandidate === undefined) {
          if (!isEmptyAllowed) {
            onChange(takeNumber(o), defaultValue);
            return;
          }
        } else {
          if (min > newCandidate || max < newCandidate) {
            return;
          }
        }
        onChange(takeNumber(o), newCandidate);
      }}
      validationRules={validationRules}
      skipInitialValidation={skipInitialValidation}
      onValidation={onValidation}
      onEnter={onEnter}
      onFocusLoss={() => {
        if (typeof value === "string" && value !== textInput) {
          setTextInput(value);
        }
      }}
    />
  );
};

export const IntegerInput = ({
  disabled,
  id,
  name,
  value,
  isEmptyAllowed = true,
  defaultValue = 0,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  placeholder,
  validationRules,
  skipInitialValidation,
  onValidation,
  onChange,
  onEnter,
  className,
  style,
}) => (
  <InternalNumberInput
    disabled={disabled}
    validPatterns={[/^(-|0|-?[1-9][0-9]*)$/]}
    id={id}
    name={name}
    value={value}
    placeholder={placeholder}
    isEmptyAllowed={isEmptyAllowed}
    defaultValue={defaultValue}
    min={min}
    max={max}
    onChange={onChange}
    validationRules={validationRules}
    skipInitialValidation={skipInitialValidation}
    onValidation={onValidation}
    onEnter={onEnter}
    className={className}
    style={style}
  />
);

export const FloatInput = ({
  disabled,
  id,
  name,
  value,
  isEmptyAllowed = true,
  defaultValue = 0,
  min = Number.MIN_SAFE_INTEGER,
  max = Number.MAX_SAFE_INTEGER,
  placeholder,
  validationRules,
  skipInitialValidation,
  onValidation,
  onChange,
  onEnter,
}) => (
  <InternalNumberInput
    disabled={disabled}
    validPatterns={[/^(-|0|-?[0-9]+\.?[0-9]*)$/]}
    id={id}
    name={name}
    value={value}
    placeholder={placeholder}
    isEmptyAllowed={isEmptyAllowed}
    defaultValue={defaultValue}
    min={min}
    max={max}
    onChange={onChange}
    validationRules={validationRules}
    skipInitialValidation={skipInitialValidation}
    onValidation={onValidation}
    onEnter={onEnter}
  />
);

export const Checkbox = ({
  disabled,
  id,
  name,
  value,
  validationRules,
  skipInitialValidation,
  onValidation,
  onChange,
  onEnter,
}) => {
  const isInit = useRef(true);
  const initialValue = useRef(value);
  const [hasError, setHasError] = useState(false);
  const onValidationWrapper = (isValid, failedResults) => {
    setHasError(!isValid);
    if (onValidation) {
      onValidation(isValid, failedResults);
    }
  };
  useEffect(() => {
    if (validationRules) {
      if (isInit.current && skipInitialValidation && initialValue.current === value) {
        return;
      }
      const failedResults = validate(value, validationRules);
      onValidationWrapper(failedResults.length === 0, failedResults);
    }
  }, []);
  useEffect(() => {
    if (isInit.current && initialValue.current !== value) {
      isInit.current = false;
    }
  }, [value]);

  const classNames = [
    "checkbox",
    hasError ? "has-errors" : "",
    disabled ? "checkbox-disabled" : "checkbox-not-disabled",
    value ? "checkbox-checked" : "",
  ];

  return (
    <div
      className={classNames.join(" ")}
      onClick={() => (disabled ? null : onChange(!value))}
      onBlur={disabled ? undefined : onEnter}
      onKeyDown={(e) => {
        if (!disabled && onEnter && e.key === "Enter") {
          onEnter();
        }
      }}>
      {value ? (
        <div>
          <FiCheck />
        </div>
      ) : (
        <></>
      )}
      <input type="checkbox" disabled={disabled} id={id} name={name} checked={value} readOnly />
    </div>
  );
};

export const RadioOptionInput = ({
  disabled,
  id,
  name,
  value,
  optionValue,
  validationRules,
  skipInitialValidation,
  onValidation,
  onChange,
  onEnter,
}) => {
  const isInit = useRef(true);
  const initialValue = useRef(value);
  const [hasError, setHasError] = useState(false);
  const isSelected = value === optionValue;

  const onValidationWrapper = (isValid, failedResults) => {
    setHasError(!isValid);
    if (onValidation) {
      onValidation(isValid, failedResults);
    }
  };
  useEffect(() => {
    if (validationRules) {
      if (isInit.current && skipInitialValidation && initialValue.current === value) {
        return;
      }
      const failedResults = validate(value, validationRules);
      onValidationWrapper(failedResults.length === 0, failedResults);
    }
  }, []);
  useEffect(() => {
    if (isInit.current && initialValue.current !== value) {
      isInit.current = false;
    }
  }, [value]);

  const classNames = [
    "radio-option",
    hasError ? "has-errors" : "",
    disabled ? "radio-option-disabled" : "radio-option-not-disabled",
    isSelected ? "radio-option-selected" : "",
  ];

  return (
    <div
      className={classNames.join(" ")}
      onClick={() => (disabled ? null : onChange(optionValue))}
      onBlur={disabled ? undefined : onEnter}
      onKeyDown={(e) => {
        if (!disabled && onEnter && e.key === "Enter") {
          onEnter();
        }
      }}>
      {isSelected ? <div className="checkmark"></div> : <></>}
      <input
        type="radio"
        disabled={disabled}
        id={id}
        name={name}
        checked={isSelected}
        value={optionValue}
        readOnly
      />
    </div>
  );
};

const TextHilighter = ({ text, search }) => {
  if (search.length === 0) {
    return <>{text}</>;
  }
  const regExp = new RegExp(`(${search})`, "i");
  return (
    <>
      {text.split(regExp).flatMap((x, i) =>
        regExp.test(x) ? (
          <span key={`${i}`} className="highlight">
            {x}
          </span>
        ) : (
          x
        ),
      )}
    </>
  );
};

const optionKeyMaker = (key) => `$${key}`;

const selectInputLabels = {
  loading: "Loading...",
  loadingError: "An error occured",
  noOptons: "No options",
};

const multiSelectInputLabels = {
  ...selectInputLabels,
  nSelectedValues: (total) => `${total} selected`,
};

const multiSelectComponents = {
  InputFactory: (labels) => (props) =>
    !props.selectProps.value || props.selectProps.value.length < 4 ? (
      <components.ValueContainer {...props} />
    ) : (
      <components.ValueContainer {...props}>
        {labels.nSelectedValues(props.selectProps.value.length)}
        {props.children[1]}
      </components.ValueContainer>
    ),
  MultiValue: (props) => (
    <Tag onClose={props.removeProps.onClick} title={props.selectProps.getOptionLabel(props.data)} />
  ),
  Option: (props) => (
    <components.Option {...props}>
      {/* <Checkbox
        id={`${props.innerProps.id}-checkbox`}
        value={props.isSelected}
        onChange={props.innerProps.onClick}
      /> */}
      {props.children}
    </components.Option>
  ),
};

export const MultiSelectInput = ({
  disabled,
  id,
  name,
  selectedKeys,
  placeholder,
  isSearchable,
  useBuiltInSearch,
  validationRules,
  skipInitialValidation,
  onValidation,
  optionProvider,
  onProviderError,
  keySelector,
  labelSelector,
  onChange,
  onNotFound,
  labels = multiSelectInputLabels,
}) => {
  const debounced = useRef(debounce(() => null));
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFailed, setIsLoadingFailed] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(undefined);
  const optionCache = useRef({});
  const valueContainer = useRef(multiSelectComponents.InputFactory(labels)).current;

  const isInit = useRef(true);
  const initialValue = useRef(selected);
  const [hasError, setHasError] = useState(false);

  const onValidationWrapper = (isValid, failedResults) => {
    setHasError(!isValid);
    if (onValidation) {
      onValidation(isValid, failedResults);
    }
  };

  // validation effect
  useEffect(() => {
    if (validationRules) {
      if (isInit.current && skipInitialValidation && initialValue.current === selected) {
        return;
      }
      const failedResults = validate(
        selected ? selected.map((x) => keySelector(x)) : undefined,
        validationRules,
      );
      onValidationWrapper(failedResults.length === 0, failedResults);
    }
  }, [selected, validationRules]);

  // init checker
  useEffect(() => {
    if (isInit.current && initialValue.current !== selected) {
      isInit.current = false;
    }
  }, [selected]);

  // clear cache when query/provider changes
  useEffect(() => {
    optionCache.current = {};
  }, [query, optionProvider]);

  // load options
  useEffect(() => {
    if (isLoadingFailed) return;
    if (Object.entries(optionCache.current).length > 0) return;

    const makeOptionCache = (options) =>
      Object.assign({}, ...options.map((x) => ({ [optionKeyMaker(keySelector(x))]: x })));

    const options = optionProvider(query);
    if (options && typeof options.then === "function") {
      setIsLoading(true);
      options
        .then((x) => (optionCache.current = makeOptionCache(x)))
        .catch((reason) => {
          setIsLoadingFailed(true);
          if (onProviderError) {
            onProviderError(reason);
          }
        })
        .finally(() => setIsLoading(false));
    } else {
      optionCache.current = makeOptionCache(options);
      setIsLoading(false);
    }
  }, [query, isLoadingFailed, keySelector, optionProvider, onProviderError]);

  // sync selected with selectedKeys
  useEffect(() => {
    if (!isLoading) {
      if (selectedKeys === undefined) {
        setSelected(undefined);
      } else {
        const selectedOptions = selectedKeys
          .map(
            (x) =>
              selected?.find((c) => x === keySelector(c)) ?? optionCache.current[optionKeyMaker(x)],
          )
          .filter((x) => x);

        if (selectedOptions.length !== selectedKeys.length && onNotFound) {
          onNotFound(
            selectedKeys.filter((x) => !selectedOptions.find((c) => keySelector(c) === x)),
          );
        }

        if (selectedOptions.length !== selected?.length) {
          setSelected(selectedOptions);
          return;
        }
        for (const option of selectedOptions) {
          if (!selected?.includes(option)) {
            setSelected(selectedOptions);
            break;
          }
        }
      }
    }
  }, [isLoading, selected, selectedKeys, keySelector, onNotFound]);

  // build react-select options
  const options = Object.entries(optionCache.current).map(([_, x]) => ({
    value: keySelector(x),
    label: labelSelector(x),
  }));

  const filterCachedOptions = (option, inputValue) =>
    optionKeyMaker(option.value) in optionCache.current &&
    option.label?.toLowerCase().includes(inputValue?.toLowerCase());

  const selectedOptions = selected
    ? selected.map((x) => ({
      value: keySelector(x),
      label: labelSelector(x),
    }))
    : null;

  return (
    <Select
      className={[
        "react-select rounded w-full min-w-60",
        "multi-select",
        hasError ? "has-errors" : "",
      ].join(" ")}
      classNamePrefix="react-select"
      components={{
        ...multiSelectComponents,
        ValueContainer: valueContainer,
      }}
      menuPosition="fixed"
      formatOptionLabel={({ label }, { inputValue }) => (
        <TextHilighter text={label} search={inputValue} />
      )}
      isMulti={true}
      hideSelectedOptions={false}
      closeMenuOnSelect={false}
      isDisabled={disabled}
      id={id}
      name={name}
      placeholder={placeholder}
      isClearable={true}
      value={selectedOptions}
      onChange={(newValue, _meta) => {
        const selectedValues = newValue || [];
        const newSelectedOptions = selectedValues.length
          ? selectedValues.map(
            (x) =>
              selected?.find((c) => x.value === keySelector(c)) ??
              optionCache.current[optionKeyMaker(x.value)],
          )
          : undefined;
        onChange(selected, newSelectedOptions, keySelector);
      }}
      options={options}
      filterOption={
        !isSearchable ? filterCachedOptions : useBuiltInSearch ? filterCachedOptions : null
      }
      onInputChange={(input, meta) => {
        if (["input-change"].includes(meta.action)) {
          if (!isLoading && isSearchable && !useBuiltInSearch && input !== query) {
            debounced.current.cancel();
            debounced.current = debounce(() => setQuery(input), 300);
            debounced.current();
          }
        }
      }}
      isLoading={isLoading}
      isSearchable={isSearchable}
      getOptionLabel={(x) => x.label}
      getOptionValue={(x) => x.value}
      loadingMessage={() => <>{labels.loading}</>}
      noOptionsMessage={() => <>{isLoadingFailed ? labels.loadingError : labels.noOptons}</>}
      onMenuOpen={() => setIsLoadingFailed(false)}
    />
  );
};

export const SelectInput = ({
  disabled,
  id,
  name,
  selectedKey,
  placeholder,
  isSearchable,
  useBuiltInSearch,
  validationRules,
  isClearable = true,
  skipInitialValidation,
  onValidation,
  optionProvider,
  onProviderError,
  keySelector,
  labelSelector,
  onChange,
  onNotFound,
  labels = selectInputLabels,
}) => {
  const debounced = useRef(debounce(() => null));
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingFailed, setIsLoadingFailed] = useState(false);
  const [query, setQuery] = useState(selectedKey);
  const [selected, setSelected] = useState(undefined);
  const optionCache = useRef({});
  const [, setCacheVersion] = useState(0);

  const isInit = useRef(true);
  const initialValue = useRef(selected);
  const [hasError, setHasError] = useState(false);
  const onValidationWrapper = (isValid, failedResults) => {
    setHasError(!isValid);
    if (onValidation) {
      onValidation(isValid, failedResults);
    }
  };

  useEffect(() => {
    if (validationRules) {
      if (isInit.current && skipInitialValidation && initialValue.current === selected) {
        return;
      }
      const failedResults = validate(selected ? keySelector(selected) : undefined, validationRules);
      onValidationWrapper(failedResults.length === 0, failedResults);
    }
  }, [selected, validationRules]);

  useEffect(() => {
    if (isInit.current && initialValue.current !== selected) {
      isInit.current = false;
    }
  }, [selected]);

  useEffect(() => {
    optionCache.current = {};
  }, [query, optionProvider]);

  useEffect(() => {
    if (isLoadingFailed) {
      return;
    }
    if (Object.entries(optionCache.current).length > 0) {
      return;
    }
    const makeOptionCache = (options) =>
      Object.assign({}, ...options.map((x) => ({ [optionKeyMaker(keySelector(x))]: x })));
    const options = optionProvider(query);
    if (options instanceof Promise) {
      setIsLoading(true);
      options
        .then((x) => (optionCache.current = makeOptionCache(x)))
        .catch((reason) => {
          setIsLoadingFailed(true);
          if (onProviderError) {
            onProviderError(reason);
          }
        })
        .finally(() => setIsLoading(false));
    } else {
      optionCache.current = makeOptionCache(options);
      setCacheVersion((v) => v + 1);
      setIsLoading(false);
    }
  }, [
    query,
    isLoadingFailed,
    keySelector,
    optionProvider,
    onProviderError,
    setIsLoading,
    setIsLoadingFailed,
  ]);

  useEffect(() => {
    if (!isLoading) {
      if (!selected || keySelector(selected) !== selectedKey) {
        const newSelected = selectedKey
          ? optionCache.current[optionKeyMaker(selectedKey)]
          : undefined;
        setSelected(newSelected);
        if (!newSelected && selectedKey !== undefined && onNotFound) {
          onNotFound(selectedKey);
        }
      }
    }
  }, [isLoading, selected, selectedKey, keySelector, setSelected, onNotFound]);
  const options = [
    ...Object.entries(optionCache.current).map(([_, x]) => ({
      value: keySelector(x),
      label: labelSelector(x),
    })),
  ];
  const filterCachedOptions = (option, inputValue) =>
    optionKeyMaker(option.value) in optionCache.current &&
    option.label.toLowerCase().includes(inputValue.toLowerCase());
  const selectedOption = selected
    ? { value: keySelector(selected), label: labelSelector(selected) }
    : null;
  return (
    <>
      <Select
        className={[
          "react-select rounded w-full min-w-60 h-10",
          "single-select",
          hasError ? "has-errors" : "",
        ].join(" ")}
        menuPosition="fixed"
        isDisabled={disabled}
        styles={{
          control: (provided, state) => ({
            ...provided,
            fontSize: "14px",
            boxShadow: state.isFocused ? "none" : provided.boxShadow,
            borderColor: state.isFocused ? "#aaa" : provided.borderColor,
            "&:hover": {
              borderColor: "#aaa",
              cursor: "pointer",
            },
          }),
          menu: (provided) => ({
            ...provided,
            fontSize: "14px",
            zIndex: 10,
          }),
          singleValue: (provided) => ({
            ...provided,
            fontSize: "14px",
          }),
          placeholder: (provided) => ({
            ...provided,
            fontSize: "14px",
          }),
          input: (provided) => ({
            ...provided,
            fontSize: "14px",
          }),
        }}
        id={id}
        name={name}
        placeholder={placeholder}
        isClearable={isClearable}
        value={selectedOption}
        onChange={(newValue, _meta) => {
          const newSelectedOption = newValue
            ? optionCache.current[optionKeyMaker(newValue.value)]
            : undefined;
          onChange(selected, newSelectedOption, keySelector);
        }}
        options={options}
        filterOption={
          !isSearchable ? filterCachedOptions : useBuiltInSearch ? filterCachedOptions : null
        }
        onInputChange={(input, meta) => {
          if (["input-change"].includes(meta.action))
            if (!isLoading && isSearchable && !useBuiltInSearch && input !== query) {
              debounced.current.cancel();
              debounced.current = debounce(() => setQuery(input), 300);
              debounced.current();
            }
        }}
        isLoading={isLoading}
        isSearchable={isSearchable ? true : false}
        getOptionLabel={(x) => x.label}
        getOptionValue={(x) => x.value}
        loadingMessage={(_input) => <>{labels.loading}</>}
        noOptionsMessage={(_input) => (
          <>{isLoadingFailed ? labels.loadingError : labels.noOptons}</>
        )}
        onMenuOpen={() => setIsLoadingFailed(false)}
      />
    </>
  );
};

export const DateInput = ({
  disabled = false,
  id,
  name,
  value,
  placeholder,
  validationRules,
  skipInitialValidation,
  onValidation,
  onChange,
  onEnter,
  minDate,
  maxDate,
  open,
  onCalendarClose,
}) => {
  const [selected, setSelected] = useState(undefined);

  const isInit = useRef(true);
  const initialValue = useRef(value);
  const [hasError, setHasError] = useState(false);

  const onValidationWrapper = (isValid) => {
    setHasError(!isValid);
    if (onValidation) {
      onValidation(isValid);
    }
  };

  useEffect(() => {
    if (validationRules) {
      if (isInit.current && skipInitialValidation && initialValue.current === value) {
        return;
      }
      const failedResults = validate(value ? value : null);
      onValidationWrapper(failedResults.length === 0, failedResults);
    }
  }, []);

  useEffect(() => {
    if (isInit.current && initialValue.current !== value) {
      isInit.current = false;
    }
  }, [value]);

  useEffect(() => {
    setSelected(value);
  }, [value]);

  const CustomInput = forwardRef((props, ref) => {
    const validationRuleProxy = { name: "proxy", message: "proxy", run: () => !hasError };
    return (
      <div
        className={[
          "custom-input flex w-full min-w-60  items-center justify-between  h-[36px] focus:font-bold ",
          disabled ? "bg-gray-500 cursor-not-allowed" : "",
        ]}
        onClick={props.onClick}>
        <InternalInput
          ref={ref}
          type="text"
          id={props.id}
          value={props.value}
          placeholder={props.placeholder}
          onChange={props.onChange}
          validationRules={[validationRuleProxy]}
          skipInitialValidation={false}
        />
        <div className="indicators border-none text-[24px] me-2 flex cursor-pointer">
          {!disabled && value ? (
            <div
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onChange(selected, undefined);
              }}
              className="cursor-pointer">
              <IoClose />
            </div>
          ) : null}
          <IoCalendarOutline />
        </div>
      </div>
    );
  });

  return (
    <>
      <DatePicker
        wrapperClassName={[
          "react-datepicker absolute p-2 w-full",
          "single-date",
          hasError ? "has-errors" : "",
          disabled ? "bg-gray-500 cursor-not-allowed" : "",
        ].join(" ")}
        disabled={disabled}
        id={id}
        name={name}
        placeholderText={placeholder}
        selected={selected}
        minDate={minDate}
        maxDate={maxDate}
        onChange={(updated) => {
          onChange(selected, updated ?? undefined);
        }}
        customInput={<CustomInput />}
        onBlur={onEnter}
        onKeyDown={(e) => {
          if (onEnter && e.key === "Enter") {
            onEnter();
          }
        }}
        isClearable={false}
        dateFormat="dd MMM yyyy"
        {...(open === true ? { open: true } : {})}
        onCalendarClose={onCalendarClose}
        onClickOutside={onCalendarClose}
      />
    </>
  );
};
export const DateRangeInput = ({
  disabled,
  id,
  name,
  value,
  placeholder,
  validationRules,
  skipInitialValidation,
  onValidation,
  onChange,
  onEnter,
}) => {
  const [selected, setSelected] = useState(undefined);

  const isInit = useRef(true);
  const initialValue = useRef(value);
  const [hasError, setHasError] = useState(false);
  const onValidationWrapper = (isValid, failedResults) => {
    setHasError(!isValid);
    if (onValidation) {
      onValidation(isValid, failedResults);
    }
  };

  useEffect(() => {
    if (validationRules) {
      if (isInit.current && skipInitialValidation && initialValue.current === value) {
        return;
      }
      const failedResults = validate(value ? value : undefined, validationRules);
      onValidationWrapper(failedResults.length === 0, failedResults);
    }
  }, [value, validationRules]);

  useEffect(() => {
    if (isInit.current && initialValue.current !== value) {
      isInit.current = false;
    }
  }, [value]);

  useEffect(() => {
    setSelected(value);
  }, [value, setSelected]);

  const CustomInput = forwardRef((props, ref) => {
    const validationRuleProxy = { name: "proxy", message: "proxy", run: () => !hasError };
    return (
      <div
        className={[
          "custom-input flex w-full min-w-60  items-center justify-between  h-[36px] focus:font-bold ",
          disabled ? "bg-gray-500 cursor-not-allowed" : "",
        ]}
        onClick={props.onClick}>
        <InternalInput
          ref={ref}
          type="text"
          id={props.id}
          value={props.value}
          placeholder={props.placeholder}
          onChange={(_1, newValue, e) => {
            // see: https://github.com/Hacker0x01/react-datepicker/issues/3906
            const DATE_INPUT_LENGTH = 10;
            const inputs = newValue
              .split("-")
              .map((x) => x.trim())
              .filter((x) => x.length === DATE_INPUT_LENGTH);
            if (inputs.length === 2) {
              try {
                const [from, to] = [new Date(inputs[0]), new Date(inputs[1])];
                if (to >= from) {
                  onChange(selected, { from: from, to: to });
                }
              } catch {
                // do nothing
              }
            }
            props.onChange(e);
          }}
          validationRules={[validationRuleProxy]}
          skipInitialValidation={false}
        />
        <div className="indicators flex me-2">
          {!disabled && value ? (
            <div
              onClick={(e) => {
                e.preventDefault();
                onChange(selected, undefined);
              }}>
              <IoClose size={25} />
            </div>
          ) : (
            <></>
          )}
          <IoCalendarOutline size={25} />
        </div>
      </div>
    );
  });

  return (
    <DatePicker
      wrapperClassName={["react-datepicker", "date-range", hasError ? "has-errors" : ""].join(" ")}
      selectsRange={true}
      startDate={selected?.from}
      endDate={selected?.to}
      disabled={disabled}
      id={id}
      name={name}
      placeholderText={placeholder}
      // nextMonthButtonLabel={<ChevronRightIcon />}
      // previousMonthButtonLabel={<ChevronLeftIcon />}
      onChange={(updated) => onChange(selected, { from: updated[0], to: updated[1] })}
      customInput={<CustomInput />}
      onBlur={onEnter}
      onKeyDown={(e) => {
        if (onEnter && e.key === "Enter") {
          onEnter();
        }
      }}
      isClearable={false}
    />
  );
};

export const FileInput = ({
  id,
  name,
  onChange,
  validationRules,
  onValidation,
  value,
  skipInitialValidation,
  placeholder,
  error,
  accept,
  helperText,
}) => {
  const isInit = useRef(true);
  const initialValue = useRef(value);
  const [fileName, setFileName] = useState(placeholder);
  const onValidationWrapper = (isValid, failedResults) => {
    if (onValidation) {
      onValidation(isValid, failedResults);
    }
  };

  useEffect(() => {
    setFileName(value?.name ?? placeholder);
    if (validationRules) {
      if (isInit.current && skipInitialValidation && initialValue.current === value) {
        return;
      }
      const failedResults = validate(value ? `${value}` : undefined, validationRules);
      onValidationWrapper(failedResults.length === 0, failedResults);
    }
  }, [value, validationRules, placeholder]);

  const onInputChange = (_, __, e) => {
    const file = e?.target?.files?.[0];
    if (file?.name) {
      setFileName(file.name);
    } else {
      setFileName(placeholder);
    }
    onChange(file);
  };

  const hasFile = !!fileName && fileName !== placeholder;

  return (
    <>
      <label
        htmlFor={id}
        className={[
          "drop-file-input group relative flex w-full min-h-[132px] cursor-pointer flex-col items-center justify-center gap-2 rounded-[10px] border-2 border-dashed px-6 py-7 text-center transition-colors duration-200",
          error ? "border-red-300 bg-red-50" : "border-secondary-primary_button_bg",
          hasFile
            ? "bg-secondary-primary_button_bg/10"
            : "bg-primary-location_bg hover:border-secondary-primary_button_hover hover:bg-secondary-secondary_button",
        ].join(" ")}>
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white text-secondary-primary_button_bg shadow-sm transition-transform duration-200 group-hover:-translate-y-0.5">
          {hasFile ? <FiFileText className="text-[22px]" /> : <FiUploadCloud className="text-[22px]" />}
        </span>
        <p className="max-w-full break-all font-semibold text-black">{fileName}</p>
        {helperText ? <p className="text-sm text-gray-500">{helperText}</p> : null}
        <span className="sr-only">
          <InternalInput
            type="file"
            id={id}
            name={name}
            placeholder={placeholder}
            accept={accept}
            onChange={(newValue, oldValue, e) => onInputChange(newValue, oldValue, e)}
            validationRules={validationRules}
            skipInitialValidation={skipInitialValidation}
            onValidation={onValidation}
          />
        </span>
      </label>
      {error ? <p className="file-upload-error mt-1.5 text-sm text-red-500">{error}</p> : null}
    </>
  );
};

export const CheckboxMultiSelectInput = ({
  disabled,
  id,
  name,
  selectedKeys = [],
  placeholder,
  optionProvider,
  keySelector,
  labelSelector,
  onChange,
  validationRules,
  skipInitialValidation,
  onValidation,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const containerRef = useRef(null);
  const isInit = useRef(true);
  const initialKeys = useRef(selectedKeys);

  const onValidationWrapper = useCallback((isValid, failedResults) => {
    setHasError(!isValid);
    if (onValidation) onValidation(isValid, failedResults);
  }, [onValidation]);

  useEffect(() => {
    if (validationRules) {
      if (isInit.current && skipInitialValidation && initialKeys.current === selectedKeys) return;
      const failedResults = validate(selectedKeys, validationRules);
      onValidationWrapper(failedResults.length === 0, failedResults);
    }
  }, [selectedKeys, validationRules]);

  useEffect(() => {
    if (isInit.current && initialKeys.current !== selectedKeys) isInit.current = false;
  }, [selectedKeys]);

  useEffect(() => {
    const result = optionProvider();
    if (result && typeof result.then === 'function') {
      setIsLoading(true);
      result.then((opts) => { setOptions(opts); setIsLoading(false); }).catch(() => setIsLoading(false));
    } else {
      setOptions(result || []);
      setIsLoading(false);
    }
  }, [optionProvider]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleOption = (key) => {
    if (disabled) return;
    const next = selectedKeys.includes(key)
      ? selectedKeys.filter((k) => k !== key)
      : [...selectedKeys, key];
    onChange(selectedKeys, next);
  };

  const selectedLabels = selectedKeys
    .map((k) => {
      const opt = options.find((o) => keySelector(o) === k);
      return opt ? labelSelector(opt) : k;
    });

  const triggerLabel = selectedKeys.length === 0
    ? placeholder
    : selectedLabels.length <= 2
      ? selectedLabels.join(', ')
      : `${selectedLabels.slice(0, 2).join(', ')} +${selectedLabels.length - 2}`;

  const hasSelected = selectedKeys.length > 0;

  return (
    <div ref={containerRef} className="relative w-full" id={id}>
      <button
        type="button"
        name={name}
        disabled={disabled}
        onClick={() => setIsOpen((v) => !v)}
        className={[
          'flex items-center justify-between w-full h-10 px-3 rounded border-2 text-sm font-medium transition-colors',
          hasSelected
            ? 'bg-secondary-primary_button_bg text-white border-secondary-primary_button_bg'
            : 'bg-white text-gray-500 border-gray-300',
          hasError ? 'border-red-500' : '',
          disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-[#94c0c0]',
        ].join(' ')}>
        <span className="truncate">{triggerLabel}</span>
        <svg
          className={`w-4 h-4 ml-2 flex-shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white rounded-lg shadow-lg border border-gray-200 py-1 max-h-60 overflow-y-auto">
          {isLoading ? (
            <div className="px-3 py-2 text-sm text-gray-400">Loading...</div>
          ) : options.length === 0 ? (
            <div className="px-3 py-2 text-sm text-gray-400">No options</div>
          ) : (
            options.map((opt) => {
              const key = keySelector(opt);
              const label = labelSelector(opt);
              const checked = selectedKeys.includes(key);
              return (
                <div
                  key={key}
                  onClick={() => toggleOption(key)}
                  className="flex items-center gap-3 px-4 py-2.5 cursor-pointer hover:bg-gray-50 select-none">
                  <div className={[
                    'w-5 h-5 rounded flex items-center justify-center border-2 flex-shrink-0 transition-colors',
                    checked ? 'bg-secondary-primary_button_bg border-secondary-primary_button_bg' : 'bg-white border-gray-300',
                  ].join(' ')}>
                    {checked && (
                      <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm text-gray-700">{label}</span>
                </div>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

export function ToggleButton({ checked, onChange, disabled = false }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`
        w-12 h-6 rounded-full border-none relative transition-colors duration-300
        ${checked ? "bg-secondary-primary_button_bg" : "bg-gray-300"}
        ${disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"}
      `}>
      <span
        className={`
          absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-md
          transition-all duration-300
          ${checked ? "left-[26px]" : "left-0.5"}
        `}
      />
    </button>
  );
}
